import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { treatmentDetails, clinicInfo } from '../data/mockData';
import { useParams } from 'react-router-dom';

const TreatmentDetailPage = () => {
  const { id } = useParams();
  const treatment = treatmentDetails[id];

  if (!treatment) {
    return (
      <div className="min-h-screen bg-background py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-primary mb-4">Treatment Not Found</h1>
          <Link to="/treatments">
            <Button className="text-white" size="lg">
              Back to Treatments
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment for ${treatment.name}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/treatments" className="inline-flex items-center text-primary hover:text-primary/80 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Treatments
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            {treatment.name}
          </h1>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">
                Common Conditions
              </h2>
              {/* <ul className="space-y-3">
                {treatment.painPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{point}</span>
                  </li>
                ))}
              </ul> */}
              <ul className="space-y-3">
                {treatment.commonConditions.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{point}</span>
                  </li>
                ))}
              </ul>

            </div>
            <Card className="bg-secondary border-none">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-primary mb-4">
                  Book Your Assessment
                </h3>
                <p className="text-muted-foreground mb-6">
                  Get a comprehensive evaluation and personalized treatment plan for your condition.
                </p>
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  className="w-full text-white"
                >
                  Book Appointment
                </Button>
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Typically replies within minutes
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            Our Approach
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-4xl">
            {treatment.approach}
          </p>
        </div>
      </section>

      {/* Recovery Path */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
            Your Recovery Path
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {treatment.recoveryPath.map((phase, index) => (
              <Card key={index} className="border-2 border-border hover:border-primary transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">
                    {phase.phase}
                  </h3>
                  <p className="text-muted-foreground">{phase.focus}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="py-12 md:py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            Expected Outcomes
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            {treatment.outcomes}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Begin Your Recovery?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Schedule your comprehensive assessment and take the first step towards pain-free movement.
          </p>
          <Button
            onClick={handleWhatsApp}
            size="lg"
            className="text-white text-lg px-8 py-6 transition-all duration-300"
          >
            Book Appointment Now
          </Button>
        </div>
      </section>
    </div>
  );
};

export default TreatmentDetailPage;
